import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useNavigate,useLocation } from 'react-router-dom';
import navLogo from '../assets/images/Logos/kole-medical-logo-full.svg';
import profilePic from '../assets/images/profile/account_circle_60dp_FFFFFF_FILL1_wght500_GRAD200_opsz48.png';
import adminIcon from '../assets/images/Logos/ADMIN label.png'
import { useAuth } from "../context/AuthContext";
import {  collection, limit, onSnapshot, orderBy, query, where } from "firebase/firestore";
import '../styles/Navbar.css';
import getDisplayName from "../utils/helper";
import StatusModal from './Overlay/Statusmodal';
import { db } from "../firebase/config";

const activeClass = ({ isActive }: { isActive: boolean }) => (isActive ? "active" : "");

const Navbar = () => {
const [menuOpen, setMenuOpen] = useState(false);
const { user, userData, logout } = useAuth();
const navigate = useNavigate();
const location = useLocation();
const isDashboard = location.pathname.startsWith("/dashboard"); // Check if we're in the dashboard section
// Track whether dropdown is open
const [profileOpen, setProfileOpen] = useState(false);
const profileRef = useRef<HTMLDivElement>(null);
const role = userData?.role; // "client" | "admin" | "superadmin"
const userId = userData?.userId; // Firestore document ID

// Local state for the latest records
const [latestApplication, setLatestApplication] = useState<any>(null);
const [latestQuote, setLatestQuote] = useState<any>(null);
const [activeModal, setActiveModal] = useState<"application" | "quote" | null>(null);
  

// ── Fetch latest application for this user (real-time) ──
useEffect(() => {
  if (!userId) return;
 
  const q = query(
    collection(db, "applications"),
    where("userId", "==", userId),    // only this user's applications
    orderBy("createdAt", "desc"),     // most recent first
    limit(1)                          // we only need the latest one
  );
 
  const unsubscribe = onSnapshot(q, (snapshot) => {
    if (!snapshot.empty) {
      const doc = snapshot.docs[0];
      setLatestApplication({ id: doc.id, ...doc.data() });
    } else {
      setLatestApplication(null); // user has no applications yet
    }
  });
 
  return () => unsubscribe(); // cleanup on unmount
}, [userId]);
 
// ── Fetch latest quote for this user (real-time) ──
useEffect(() => {
  if (!userId) return;
 
  const q = query(
    collection(db, "quotes"),
    where("userId", "==", userId),    // only this user's quotes
    orderBy("createdAt", "desc"),     // most recent first
    limit(1)                          // we only need the latest one
  );
 
  const unsubscribe = onSnapshot(q, (snapshot) => {
    if (!snapshot.empty) {
      const doc = snapshot.docs[0];
      setLatestQuote({ id: doc.id, ...doc.data() });
    } else {
      setLatestQuote(null); // user has no quotes yet
    }
  });
 
  return () => unsubscribe();
}, [userId]);
 

// Close dropdown when user clicks outside
useEffect(() => {
  const handleClickOutside = (e: MouseEvent) => {
    if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
      setProfileOpen(false);
    }
  };
  document.addEventListener("mousedown", handleClickOutside);
  return () => document.removeEventListener("mousedown", handleClickOutside);
}, []);


const handleLogout = async () => {
    await logout();
    navigate("/"); // redirect after logout
  };

// Map account status to a CSS class
const getStatusClass = (status?: string) => {
  switch (status) {
    case "active":    return "status-active";
    case "suspended": return "status-suspended";
    case "pending":   return "status-pending";
    case "rejected":  return "status-rejected";
    default:          return "status-default"; // logged out
  }
};

const AdminMenu = (
  <>
    <button
      className="dropdown-item"
      onClick={() => { navigate("/dashboard"); setProfileOpen(false); }}
    >
      <span className="di-icon">🏠</span>
      Dashboard
    </button>

    <button
      className="dropdown-item"
      onClick={() => { navigate("/dashboard/applications"); setProfileOpen(false); }}
    >
      <span className="di-icon">📄</span>
      Applications
      <span className="di-badge di-badge-blue">-</span>
    </button>

    <button
      className="dropdown-item"
      onClick={() => { navigate("/dashboard/Quotes"); setProfileOpen(false); }}
    >
      <span className="di-icon">💰</span>
      Quotations
      <span className="di-badge">-</span>
    </button>

    <button
      className="dropdown-item"
      onClick={() => { navigate("/dashboard/users"); setProfileOpen(false); }}
    >
      <span className="di-icon">👥</span>
      Manage Users
    </button>

    <button
      className="dropdown-item"
      onClick={() => { navigate("/admin/notifications"); setProfileOpen(false); }}
    >
      <span className="di-icon">🔔</span>
      Notifications
      <span className="di-badge">-</span>
    </button>

    {/*{role === "superadmin" && (
      <button
        className="dropdown-item"
        onClick={() => { navigate("/admin/reports"); setProfileOpen(false); }}
      >
        <span className="di-icon">📊</span>
        Reports & Analytics
      </button>
    )}

    <button
      className="dropdown-item"
      onClick={() => { navigate("/admin/settings"); setProfileOpen(false); }}
    >
      <span className="di-icon">⚙️</span>
      System Settings
    </button>*/}

    <div className="dropdown-divider" />

    <button
      className="dropdown-item danger"
      onClick={() => { handleLogout(); setProfileOpen(false); }}
    >
      <span className="di-icon">⏻</span>
      Sign Out
    </button>
  </>
);

const ClientMenu = (
  <>
    
    <div
      className="dropdown-status-card"
      onClick={() => latestQuote && setActiveModal("quote")}
      style={{ cursor: latestQuote ? "pointer" : "default" }}
    >
      <div className="dsc-label">
        <span>💰</span> Latest Quotation
      </div>
      <div className="dsc-row">
        {latestQuote ? (
          <>
            <span className="dsc-id">{latestQuote.id}</span>
            <span className={`dsc-badge badge-${latestQuote.status}`}>
              {latestQuote.status}
            </span>
          </>
        ) : (
          <span className="dsc-empty">No quotation yet</span>
        )}
      </div>
      {latestQuote && (
        <div className="dsc-hint">👆 Tap to view progress</div>
      )}
    </div>
    
    <div
      className="dropdown-status-card"
      onClick={() => latestApplication && setActiveModal("application")}
      style={{ cursor: latestApplication ? "pointer" : "default" }}
    >
      <div className="dsc-label">
        <span>📄</span> Latest Application
      </div>
      <div className="dsc-row">
        {latestApplication ? (
          <>
            <span className="dsc-id">{latestApplication.id}</span>
            <span className={`dsc-badge badge-${latestApplication.status}`}>
              {latestApplication.status.replace("_", " ")}
            </span>
          </>
        ) : (
          <span className="dsc-empty">No application yet</span>
        )}
      </div>
      {latestApplication && (
        <div className="dsc-hint">👆 Tap to view progress</div>
      )}
    </div>

    <div className="dropdown-divider" />

    {/*<button
      className="dropdown-item"
      onClick={() => { navigate("/applications"); setProfileOpen(false); }}
    >
      <span className="di-icon">📋</span>
      My Applications
    </button>*/}

    <button
      className="dropdown-item"
      onClick={() => { navigate("/quote"); setProfileOpen(false); }}
    >
      <span className="di-icon">💬</span>
      Request a Quote
    </button>

    {/*<button
      className="dropdown-item"
      onClick={() => { navigate("/documents"); setProfileOpen(false); }}
    >
      <span className="di-icon">📁</span>
      My Documents
    </button>*/}

    {/*<button
      className="dropdown-item"
      onClick={() => { navigate("/notifications"); setProfileOpen(false); }}
    >
      <span className="di-icon">🔔</span>
      Notifications
      <span className="di-badge">3</span>
    </button>*/}

    <button
      className="dropdown-item"
      onClick={() => { navigate("/support"); setProfileOpen(false); }}
    >
      <span className="di-icon">🛟</span>
      Support & Queries
    </button>

    {/*<button
      className="dropdown-item"
      onClick={() => { navigate("/profile"); setProfileOpen(false); }}
    >
      <span className="di-icon">👤</span>
      My Profile
    </button>*/}

    <div className="dropdown-divider" />

    <button
      className="dropdown-item danger"
      onClick={() => { handleLogout(); setProfileOpen(false); }}
    >
      <span className="di-icon">⏻</span>
      Sign Out
    </button>
  </>
);

const RoleBasedMenu = (
  <>
    {user ? (
      role === "admin" || role === "superadmin" ? AdminMenu : ClientMenu
    ) : (
      <button
        className="dropdown-item"
        onClick={() => { navigate("/login"); setProfileOpen(false); }}
      >
        <span className="di-icon">🔑</span>
        Sign In
      </button>
    )}
  </>
);

    return (
        <nav className="navbarContainer" style={styles.nav}>
            <div className="logo">
              <Link to="/">
              <img style={styles.logo} src={navLogo} alt="Kole Medical" width={200} height={66} />
              </Link>
              
            </div>

            {
              isDashboard ? /* Show different nav if we're in the dashboard section */

              (
                <div>
                  <img className="adminLogo" src={adminIcon} alt="Admin Icon" />
                </div>
               )  : /* Regular nav for non-dashboard pages */
              (<div> 
              <button
                  className={`hamburger ${menuOpen ? "open" : ""}`}
                  onClick={() => setMenuOpen(!menuOpen)}
                >
                  <span /><span /><span />
                </button>
            <ul className={`navLinks ${menuOpen ? "open" : ""}`}>
              
              <li><NavLink to="/" end     className={activeClass}>Home</NavLink></li>
              <li><NavLink to="/service" className={activeClass}>Services</NavLink></li>
              <li><NavLink to="/quote"    className={activeClass}>Request a Quote</NavLink></li>
              <li><NavLink to="/news"     className={activeClass}>News</NavLink></li>
              <li><NavLink to="/query"  className={activeClass}>Contact</NavLink></li>
              {/* On mobile the Webinars & Workshops button is hidden; this menu link replaces it */}
              <li className="navLinks-mobileOnly">
                <NavLink to="/events" className={activeClass}>
                  <span aria-hidden="true">🎓</span>Webinars &amp; Workshops
                </NavLink>
              </li>
            </ul>
            </div>
              )
            }
            {
              isDashboard ? /* Dashboard keeps the profile menu (Sign Out lives there) */

              (
            <div
            className={`userProfile ${profileOpen ? "is-open" : ""}`}
            ref={profileRef}
          >
            {/* ── AVATAR BUTTON ── */}
            <div
              className="avatar-wrapper"
              onClick={() => setProfileOpen(!profileOpen)}
            >
              {/* Announce ring — pulses to draw attention */}
              <span className="announce-ring" />
        
              {/* Profile image with status border */}
              <img
                src={profilePic}
                alt="Profile"
                className={getStatusClass(userData?.accountStatus)}
              />
        
              {/* Small chevron hint */}
              <span className={`avatar-chevron ${profileOpen ? "rotated" : ""}`}>
                ▾
              </span>
            </div>
        
            {/* ── WELCOME TEXT ── */}
            <div className="profile-text">
              <p className="welcome-label">Welcome</p>
              <p className="profile-name">
                {userData ? getDisplayName(userData.fullName) : "Guest"}
              </p>
            </div>
        
            {/* ── DROPDOWN PANEL ── */}
            <div className={`profile-dropdown ${profileOpen ? "dropdown-open" : ""}`}>
        
              {/* User info header */}
              <div className="dropdown-header">
                <img
                  src={profilePic}
                  alt="Profile"
                  className={`dropdown-avatar ${getStatusClass(userData?.accountStatus)}`}
                />
                <div>
                  <p className="dropdown-name">
                    {userData ? userData.fullName : "Guest"}
                  </p>
                  <p className="dropdown-email">
                    {userData?.email ?? "Not signed in"}
                  </p>
                  {/* Status badge */}
                  <span className={`status-badge badge-${userData?.accountStatus ?? "default"}`}>
                    ● {userData?.accountStatus ?? "Offline"}
                  </span>
                </div>
              </div>
        
            <div className="dropdown-divider" />
            {RoleBasedMenu}
            </div>
          </div>
              ) : /* Public pages show the Webinars & Workshops button instead of the profile */
              (
                <Link to="/events" className="webinarsBtn">
                  <span className="webinarsBtn-icon" aria-hidden="true">🎓</span>
                  <span className="webinarsBtn-label">Webinars & Workshops</span>
                </Link>
              )
            }
          {/* ── Modals — place at the bottom of your Navbar return ── */}
          
          {activeModal === "quote" && latestQuote && (
            <StatusModal
              type="quote"
              data={latestQuote}
              onClose={() => setActiveModal(null)}
            />
          )}
          
          {activeModal === "application" && latestApplication && (
            <StatusModal
              type="application"
              data={latestApplication}
              onClose={() => setActiveModal(null)}
            />
          )}
        </nav>
    );
};

const styles = {
  nav: {
    /*display: "flex",
    justifyContent: "space-between",*/
    
  },
  logo: {
    margin: 0,
    padding: 0,
    width: "200px",
    height: "auto"
  }
};

export default Navbar;